import React, { useState, useEffect, useRef } from 'react';
import { MdClose, MdArrowBack, MdArrowForward, MdMenuBook, MdOutlineCheckBox, MdVisibility } from 'react-icons/md';
import Backdrop from 'components/Backdrop';
import Button from 'components/Button';

import './index.css';

const SLIDES = [
    {
        title: 'Welcome to Vocab',
        subtitle: 'A quick overview of the vocabulary flashcard feature',
        content: (
            <div className='vw__welcome'>
                <div className='vw__welcomeIcon'><MdMenuBook /></div>
                <p>The Vocab feature lets you assign vocabulary flashcard sets to your students. Students study using an interactive card-sort system and you can track their progress in real time.</p>
                <div className='vw__welcomeHighlights'>
                    <div className='vw__welcomeItem'>
                        <span className='vw__welcomeNumber'>800</span>
                        <span className='vw__welcomeLabel'>SAT vocab words</span>
                    </div>
                    <div className='vw__welcomeItem'>
                        <span className='vw__welcomeNumber'>40</span>
                        <span className='vw__welcomeLabel'>vocab sets</span>
                    </div>
                    <div className='vw__welcomeItem'>
                        <span className='vw__welcomeNumber'>20</span>
                        <span className='vw__welcomeLabel'>words per set</span>
                    </div>
                </div>
            </div>
        ),
    },
    {
        title: 'Step 1: Navigate to Vocab',
        subtitle: 'Tutor side',
        content: (
            <div className='vw__slide'>
                <div className='vw__mockSidebar'>
                    <div className='vw__mockSidebarItem'>Tests</div>
                    <div className='vw__mockSidebarItem'>Assignments</div>
                    <div className='vw__mockSidebarItem vw__mockSidebarItem--active'>
                        <MdMenuBook /> Vocab
                    </div>
                    <div className='vw__mockSidebarItem'>Students</div>
                </div>
                <div className='vw__slideText'>
                    <p>Click <strong>Vocab</strong> in the sidebar to open the vocab management page. You'll see a list of all available vocab sets organized by subject.</p>
                </div>
            </div>
        ),
    },
    {
        title: 'Step 2: Preview a Vocab Set',
        subtitle: 'Tutor side',
        content: (
            <div className='vw__slide'>
                <div className='vw__mockTable'>
                    <div className='vw__mockRow vw__mockRow--header'>
                        <span>Subject</span>
                        <span># Words</span>
                        <span>Preview</span>
                        <span>Assign</span>
                    </div>
                    <div className='vw__mockRow'>
                        <span>Positive Trait Words</span>
                        <span>20</span>
                        <span className='vw__mockPreview'><MdVisibility /></span>
                        <span className='vw__mockAssign'>Assign</span>
                    </div>
                    <div className='vw__mockRow'>
                        <span>Academic Verbs</span>
                        <span>20</span>
                        <span className='vw__mockPreview'><MdVisibility /></span>
                        <span className='vw__mockAssign'>Assign</span>
                    </div>
                </div>
                <div className='vw__slideText'>
                    <p>Click the <MdVisibility style={{ verticalAlign: 'middle' }} /> <strong>eye icon</strong> to preview all words in a set before assigning. This opens a popup showing every word, its definition, part of speech, and connotation.</p>
                </div>
            </div>
        ),
    },
    {
        title: 'Step 3: Assign to Students',
        subtitle: 'Tutor side',
        content: (
            <div className='vw__slide'>
                <div className='vw__mockAssignFlow'>
                    <div className='vw__mockPanel'>
                        <div className='vw__mockPanelHeader'>Vocab Sets</div>
                        <div className='vw__mockRow vw__mockRow--selected'>Positive Trait Words</div>
                        <div className='vw__mockRow'>Academic Verbs</div>
                    </div>
                    <div className='vw__mockArrow'>&#8250;&#8250;</div>
                    <div className='vw__mockPanel'>
                        <div className='vw__mockPanelHeader'>Students</div>
                        <div className='vw__mockStudentRow'>
                            <MdOutlineCheckBox style={{ color: '#0575a2' }} /> John Smith
                        </div>
                        <div className='vw__mockStudentRow'>
                            <MdOutlineCheckBox style={{ color: '#0575a2' }} /> Jane Doe
                        </div>
                    </div>
                </div>
                <div className='vw__slideText'>
                    <p>Click <strong>Assign</strong> on a vocab set to see your students. Check the box next to each student to assign the set. The student list shows who is already assigned and their current status.</p>
                    <p>Click the <strong>arrow</strong> between panels or click the selected row again to go back.</p>
                </div>
            </div>
        ),
    },
    {
        title: 'Step 4: Student Opens Vocab',
        subtitle: 'What students see',
        content: (
            <div className='vw__slide'>
                <div className='vw__mockDeckGrid'>
                    <div className='vw__mockDeck'>
                        <div className='vw__mockDeckStack'>
                            <div className='vw__mockCard vw__mockCard--3'></div>
                            <div className='vw__mockCard vw__mockCard--2'></div>
                            <div className='vw__mockCard vw__mockCard--1'>
                                <MdMenuBook />
                                <span>20 cards</span>
                            </div>
                        </div>
                        <p className='vw__mockDeckName'>Positive Trait Words</p>
                        <div className='vw__mockProgress'><div className='vw__mockProgressFill'></div></div>
                    </div>
                    <div className='vw__mockDeck'>
                        <div className='vw__mockDeckStack'>
                            <div className='vw__mockCard vw__mockCard--3'></div>
                            <div className='vw__mockCard vw__mockCard--2'></div>
                            <div className='vw__mockCard vw__mockCard--1'>
                                <MdMenuBook />
                                <span>20 cards</span>
                            </div>
                        </div>
                        <p className='vw__mockDeckName'>Academic Verbs</p>
                        <div className='vw__mockProgress'><div className='vw__mockProgressFill' style={{ width: '0%' }}></div></div>
                    </div>
                </div>
                <div className='vw__slideText'>
                    <p>Students see their assigned vocab sets as a <strong>deck-of-cards grid</strong>. Each deck shows the subject name, card count, and a progress bar. Decks are grouped into "In Progress", "Assigned", and "Completed" sections.</p>
                </div>
            </div>
        ),
    },
    {
        title: 'Step 5: Flashcard Study Mode',
        subtitle: 'What students see',
        content: (
            <div className='vw__slide'>
                <div className='vw__mockStudyLayout'>
                    <div className='vw__mockPile vw__mockPile--learning'>
                        <div className='vw__mockPileDot' style={{ background: '#3b82f6' }}></div>
                        <span>Still Learning</span>
                        <div className='vw__mockPileCards'>0 cards</div>
                    </div>
                    <div className='vw__mockFlashcard'>
                        <div className='vw__mockFlashcardInner'>
                            <span className='vw__mockWord'>Eloquent</span>
                            <span className='vw__mockPos'>adjective</span>
                            <span className='vw__mockHint'>Click to flip</span>
                        </div>
                        <div className='vw__mockActions'>
                            <div className='vw__mockBtn vw__mockBtn--learning'>Still Learning</div>
                            <div className='vw__mockBtn vw__mockBtn--mastered'>Mastered</div>
                        </div>
                    </div>
                    <div className='vw__mockPile vw__mockPile--mastered'>
                        <div className='vw__mockPileDot' style={{ background: '#10b981' }}></div>
                        <span>Mastered</span>
                        <div className='vw__mockPileCards'>0 cards</div>
                    </div>
                </div>
                <div className='vw__slideText'>
                    <p>Students study in a <strong>three-column card-sort layout</strong>. They flip each card to see the definition, then sort it into "Still Learning" (left) or "Mastered" (right). Cards animate as they fly to each pile.</p>
                </div>
            </div>
        ),
    },
    {
        title: 'Step 6: Card Flip & Sorting',
        subtitle: 'What students see',
        content: (
            <div className='vw__slide'>
                <div className='vw__mockFlipDemo'>
                    <div className='vw__mockFlipCard vw__mockFlipCard--front'>
                        <span className='vw__mockWord'>Eloquent</span>
                        <span className='vw__mockPos'>adjective</span>
                    </div>
                    <div className='vw__mockFlipArrow'>&#8594;</div>
                    <div className='vw__mockFlipCard vw__mockFlipCard--back'>
                        <span className='vw__mockDef'>Fluent or persuasive in speaking or writing</span>
                        <span className='vw__mockConnotation'>Connotation: <strong style={{ color: '#059669' }}>positive</strong></span>
                    </div>
                </div>
                <div className='vw__slideText'>
                    <p>The front shows the <strong>word and part of speech</strong>. Clicking flips to reveal the <strong>definition, connotation, and example sentence</strong>.</p>
                    <p><strong>Keyboard shortcuts:</strong> Space = flip, Arrows = navigate, 1 = Still Learning, 2 = Mastered</p>
                </div>
            </div>
        ),
    },
    {
        title: 'Step 7: Completion & Mastery',
        subtitle: 'What students see',
        content: (
            <div className='vw__slide'>
                <div className='vw__mockCompletion'>
                    <div className='vw__mockCompletionIcon'>&#127881;</div>
                    <h3>You mastered the set!</h3>
                    <p className='vw__mockCompletionText'>When all words are mastered, students see a congratulations popup with the option to complete the set or continue studying.</p>
                    <div className='vw__mockCompletionBtns'>
                        <div className='vw__mockBtn vw__mockBtn--mastered'>Complete Set</div>
                        <div className='vw__mockBtn vw__mockBtn--outline'>Continue Studying</div>
                    </div>
                </div>
                <div className='vw__slideText'>
                    <p>Progress syncs to the server automatically. Once a student masters all words, the set moves to the <strong>"Completed"</strong> section and you'll see the updated status immediately.</p>
                </div>
            </div>
        ),
    },
    {
        title: 'Step 8: Track Progress',
        subtitle: 'Tutor side',
        content: (
            <div className='vw__slide'>
                <div className='vw__mockProgressTable'>
                    <div className='vw__mockRow vw__mockRow--header'>
                        <span>Subject</span>
                        <span>Status</span>
                        <span>Mastered</span>
                        <span>Learning</span>
                        <span>Mastery %</span>
                    </div>
                    <div className='vw__mockRow'>
                        <span>Positive Trait Words</span>
                        <span className='vw__mockStatus vw__mockStatus--completed'>Completed</span>
                        <span>20</span>
                        <span>0</span>
                        <span>100%</span>
                    </div>
                    <div className='vw__mockRow'>
                        <span>Academic Verbs</span>
                        <span className='vw__mockStatus vw__mockStatus--progress'>In Progress</span>
                        <span>12</span>
                        <span>5</span>
                        <span>60%</span>
                    </div>
                </div>
                <div className='vw__slideText'>
                    <p>Go to a student's profile and click the <strong>Vocab tab</strong> to see their progress for each assigned set. You'll see mastery percentage, how many words are mastered vs still learning, and completion status.</p>
                </div>
            </div>
        ),
    },
];

type VocabWalkthroughProps = {
    onClose: () => void;
};

function VocabWalkthrough({ onClose }: VocabWalkthroughProps) {
    const [step, setStep] = useState(0);
    const dialogRef = useRef<HTMLDivElement>(null);

    const slide = SLIDES[step];
    const isFirst = step === 0;
    const isLast = step === SLIDES.length - 1;

    // Keyboard navigation: Escape closes, arrows navigate slides
    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
            if (e.key === 'ArrowRight' && !isLast) setStep(s => s + 1);
            if (e.key === 'ArrowLeft' && !isFirst) setStep(s => s - 1);
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [isFirst, isLast, onClose]);

    // Focus the dialog on mount
    useEffect(() => {
        dialogRef.current?.focus();
    }, []);

    return (
        <>
            <Backdrop />
            <div className='vw' ref={dialogRef} tabIndex={-1} role='dialog' aria-label='Vocab feature walkthrough'>
                <div className='vw__header'>
                    <div>
                        <h1 className='vw__title'>{slide.title}</h1>
                        {slide.subtitle && <p className='vw__subtitle'>{slide.subtitle}</p>}
                    </div>
                    <button className='vw__closeBtn' onClick={onClose} aria-label='Close walkthrough' type='button'>
                        <MdClose />
                    </button>
                </div>

                <div className='vw__body'>
                    {slide.content}
                </div>

                <div className='vw__footer'>
                    <div className='vw__dots'>
                        {SLIDES.map((_, i) => (
                            <button
                                key={i}
                                className={`vw__dot ${i === step ? 'vw__dot--active' : ''}`}
                                onClick={() => setStep(i)}
                                aria-label={`Go to slide ${i + 1}`}
                                type='button'
                            />
                        ))}
                    </div>
                    <div className='vw__nav'>
                        {!isFirst && (
                            <Button secondary onClick={() => setStep(step - 1)}>
                                <MdArrowBack style={{ verticalAlign: 'middle', marginRight: 4 }} /> Back
                            </Button>
                        )}
                        {isLast ? (
                            <Button onClick={onClose}>
                                Got it!
                            </Button>
                        ) : (
                            <Button onClick={() => setStep(step + 1)}>
                                Next <MdArrowForward style={{ verticalAlign: 'middle', marginLeft: 4 }} />
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}

export default VocabWalkthrough;
